import apiService from './apiService';
import { GET, POST, PUT, PATCH, DELETE } from './executer';

export const FORECAST_TYPES = ['daily', 'hourly', 'minutely'] as const;
export type ForecastType = typeof FORECAST_TYPES[number];

export function hasForecastType(field: Pick<ForecastField, 'forecast_types'>, type: ForecastType): boolean {
  return field.forecast_types.includes(type);
}

/**
 * Forecast Field entity from the database
 */
export interface ForecastField {
  api_name: string;
  db_name: string;
  display_name: string;
  unit: string | null;
  forecast_types: ForecastType[];
  calculated: boolean; // If true, field is calculated from other data, not fetched from API
  hidden: boolean; // If true, field is not displayed in forecast view (used only for calculations)
  demo_only: boolean; // If true, field is only available for demo events (allows testing new fields)
  
  // Granular visibility controls for each view context
  show_in_hourly_cards: boolean;
  show_in_hourly_table: boolean;
  show_in_daily_cards: boolean;
  show_in_daily_table: boolean;
  show_in_map_card: boolean;
  
  // Group name for combining related fields in cards view (e.g., "temperature", "wind")
  card_group: string | null;
  
  // Short display name used when this field is shown within a group (e.g., "Min", "Max")
  group_display_name: string | null;
  
  // Position for ordering fields within a group (lower = first, null = last)
  group_position: number | null;
  
  // Overall position for ordering fields in card/table views (lower = first, null = last)
  position: number | null;

  // Soft delete flag - when true, field is excluded from default queries
  deleted?: boolean;

  // Audit fields - last update info
  updated_by?: string | null;
  update_datetime?: string | null;
}

/**
 * Request payload for creating a new forecast field
 */
export interface CreateForecastFieldRequest {
  api_name: string;
  db_name: string;
  display_name: string;
  unit?: string | null;
  forecast_types: ForecastType[];
  calculated?: boolean; // Defaults to false if not provided
  hidden?: boolean; // Defaults to false if not provided
  demo_only?: boolean; // Defaults to false if not provided
  
  // Granular visibility controls (all default to true if not provided)
  show_in_hourly_cards?: boolean;
  show_in_hourly_table?: boolean;
  show_in_daily_cards?: boolean;
  show_in_daily_table?: boolean;
  show_in_map_card?: boolean;
  
  // Group name for cards view (null = no grouping)
  card_group?: string | null;
  
  // Short display name for grouped display
  group_display_name?: string | null;
  
  // Position for ordering within groups
  group_position?: number | null;
  
  // Overall position for ordering fields
  position?: number | null;
}

/**
 * Request payload for updating a forecast field
 */
export interface UpdateForecastFieldRequest {
  db_name?: string;
  display_name?: string;
  unit?: string | null;
  forecast_types?: ForecastType[];
  calculated?: boolean;
  hidden?: boolean;
  demo_only?: boolean;
  
  // Granular visibility controls
  show_in_hourly_cards?: boolean;
  show_in_hourly_table?: boolean;
  show_in_daily_cards?: boolean;
  show_in_daily_table?: boolean;
  show_in_map_card?: boolean;
  
  // Group name for cards view
  card_group?: string | null;
  
  // Short display name for grouped display
  group_display_name?: string | null;
  
  // Position for ordering within groups
  group_position?: number | null;
  
  // Overall position for ordering fields
  position?: number | null;
}

/**
 * Request payload for batch reordering forecast fields
 */
export interface ReorderForecastFieldsRequest {
  fields: Array<{
    api_name: string;
    position: number;
  }>;
}

/**
 * Request payload for batch reordering forecast fields within a group
 */
export interface ReorderForecastFieldsInGroupRequest {
  fields: Array<{
    api_name: string;
    group_position: number;
  }>;
}

/**
 * Response from the forecast fields API
 */
export interface ForecastFieldsResponse {
  success: boolean;
  data: ForecastField[];
  count: number;
}

export interface ForecastFieldResponse {
  success: boolean;
  data: ForecastField;
}

class ForecastFieldsService {
  /**
   * Get all forecast fields
   * @param includeDeleted - If true, includes soft-deleted fields (for management UI)
   */
  async getAllForecastFields(includeDeleted: boolean = false): Promise<ForecastField[]> {
    try {
      const url = includeDeleted ? '/api/forecast-fields?include_deleted=true' : '/api/forecast-fields';
      const response = await apiService.client.execute<ForecastField[]>(
        GET,
        url
      );
      
      if (response.success && response.data) {
        return response.data;
      }
      throw new Error('Failed to fetch forecast fields');
    } catch (error) {
      console.error('Error fetching forecast fields:', error);
      throw error;
    }
  }

  /**
   * Get a forecast field by API name
   * @param includeDeleted - If true, includes soft-deleted fields
   */
  async getForecastFieldByApiName(apiName: string, includeDeleted: boolean = false): Promise<ForecastField> {
    try {
      const url = includeDeleted ? `/api/forecast-fields/${apiName}?include_deleted=true` : `/api/forecast-fields/${apiName}`;
      const response = await apiService.client.execute<ForecastField>(
        GET,
        url
      );
      
      if (response.success && response.data) {
        return response.data;
      }
      throw new Error(`Failed to fetch forecast field: ${apiName}`);
    } catch (error) {
      console.error(`Error fetching forecast field ${apiName}:`, error);
      throw error;
    }
  }

  /**
   * Create a new forecast field
   * Requires: root role
   */
  async createForecastField(fieldData: CreateForecastFieldRequest, userToken: string) {
    return await apiService.client.execute<ForecastField>(
      POST,
      '/api/forecast-fields',
      {
        data: fieldData,
        headers: {
          Authorization: `Bearer ${userToken}`,
        },
      }
    );
  }

  /**
   * Update an existing forecast field
   * Requires: root role
   */
  async updateForecastField(
    apiName: string,
    updates: UpdateForecastFieldRequest,
    userToken: string
  ) {
    return await apiService.client.execute<ForecastField>(
      PUT,
      `/api/forecast-fields/${apiName}`,
      {
        data: updates,
        headers: {
          Authorization: `Bearer ${userToken}`,
        },
      }
    );
  }

  /**
   * Delete a forecast field (soft delete)
   * Requires: root role
   */
  async deleteForecastField(apiName: string, userToken: string) {
    return await apiService.client.execute<{ message: string }>(
      DELETE,
      `/api/forecast-fields/${apiName}`,
      {
        headers: {
          Authorization: `Bearer ${userToken}`,
        },
      }
    );
  }

  /**
   * Set the soft-delete flag on a forecast field (for restore or soft-delete)
   * Requires: root role
   */
  async setForecastFieldDeleted(apiName: string, deleted: boolean, userToken: string) {
    return await apiService.client.execute<{ success: boolean; message: string }>(
      PATCH,
      `/api/forecast-fields/${apiName}/deleted`,
      {
        data: { deleted },
        headers: {
          Authorization: `Bearer ${userToken}`,
        },
      }
    );
  }

  /**
   * Batch reorder forecast fields
   * Requires: root role
   */
  async reorderForecastFields(
    fields: Array<{ api_name: string; position: number }>,
    userToken: string
  ) {
    return await apiService.client.execute<{ message: string }>(
      PUT,
      '/api/forecast-fields/reorder',
      {
        data: { fields },
        headers: {
          Authorization: `Bearer ${userToken}`,
        },
      }
    );
  }

  /**
   * Batch reorder forecast fields within a group (updates group_position)
   * Requires: root role
   */
  async reorderForecastFieldsInGroup(
    fields: Array<{ api_name: string; group_position: number }>,
    userToken: string
  ) {
    return await apiService.client.execute<{ message: string }>(
      PUT,
      '/api/forecast-fields/reorder-in-group',
      {
        data: { fields },
        headers: {
          Authorization: `Bearer ${userToken}`,
        },
      }
    );
  }
}

export const forecastFieldsService = new ForecastFieldsService();
export default forecastFieldsService;

